import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LANGUAGES } from '../i18n'
import { trackEvent } from '../lib/analytics'

const NEON = [
  { color: 'var(--neon-blue)',  rgba: 'rgba(0,234,255,0.15)'  },
  { color: 'var(--neon-pink)',  rgba: 'rgba(255,61,240,0.15)' },
  { color: 'var(--neon-green)', rgba: 'rgba(69,255,34,0.15)'  },
]

interface Rect { top: number; right: number; bottom: number }

export default function LanguageSelector() {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [rect, setRect] = useState<Rect | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0]

  const openDropdown = useCallback(() => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      setRect({ top: r.bottom + window.scrollY, right: window.innerWidth - r.right, bottom: window.innerHeight - r.bottom })
    }
    setOpen(true)
  }, [])

  useEffect(() => {
    if (!open) return
    function onOutside(e: MouseEvent) {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) setOpen(false)
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onOutside)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const panelStyle: React.CSSProperties = rect ? {
    position: 'absolute',
    top: rect.top + 8,
    right: rect.right,
    zIndex: 9999,
  } : {}

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => open ? setOpen(false) : openDropdown()}
        aria-label="Select language"
        aria-expanded={open}
        style={{
          boxShadow: open ? '4px 4px 0px 0px rgba(0,234,255,0.5)' : 'none',
          color: 'var(--neon-blue)',
          borderColor: 'var(--neon-blue)',
        }}
        className="flex items-center gap-1.5 px-3 py-2 rounded-twelve border-2 font-black uppercase tracking-widest text-xs transition-all active:scale-95 hover:bg-[var(--neon-blue)] hover:text-black"
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span>{current.code.toUpperCase()}</span>
      </button>

      {open && rect && createPortal(
        <div
          ref={panelRef}
          style={{
            ...panelStyle,
            background: 'var(--card-bg)',
            border: '2px solid rgba(255,255,255,0.15)',
            boxShadow: '8px 8px 0px 0px rgba(59,59,255,0.4)',
          }}
          className="rounded-twelve p-3 w-60"
        >
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400 mb-2 px-1">
            Language
          </p>

          <div className="overflow-y-auto [&::-webkit-scrollbar]:hidden" style={{ maxHeight: '280px', scrollbarWidth: 'none' }}>
            <div className="grid grid-cols-3 gap-1">
              {LANGUAGES.map((lang, i) => {
                const neon = NEON[i % 3]
                const isActive = lang.code === current.code
                return (
                  <LangButton
                    key={lang.code}
                    lang={lang}
                    neon={neon}
                    isActive={isActive}
                    onSelect={() => {
                    i18n.changeLanguage(lang.code)
                    navigate(lang.code === 'en' ? '/' : `/${lang.code}`)
                    setOpen(false)
                    trackEvent('language_change', { language: lang.code, language_name: lang.name })
                  }}
                  />
                )
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

function LangButton({
  lang, neon, isActive, onSelect,
}: {
  lang: typeof LANGUAGES[number]
  neon: { color: string; rgba: string }
  isActive: boolean
  onSelect: () => void
}) {
  const [hovered, setHovered] = useState(false)

  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        color: neon.color,
        borderColor: isActive || hovered ? neon.color : 'transparent',
        background: isActive || hovered ? neon.rgba : 'transparent',
      }}
      className="flex flex-col items-center gap-1 py-2 px-1 rounded-twelve border-2 font-black text-[10px] uppercase tracking-wider transition-all active:scale-95"
    >
      <span className="text-xl leading-none">{lang.flag}</span>
      <span>{lang.code.toUpperCase()}</span>
    </button>
  )
}
